import axios from 'axios';
import qs from 'qs';
import { push } from 'connected-react-router';
import { toast } from 'react-toastify';
import { normalizeMerchant } from '../utils/merchant';
//import Errors from '../components/FormItems/error/errors';

export const AUTH_FAILURE = 'AUTH_FAILURE';
export const LOGIN_REQUEST = 'LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
export const LOGOUT_REQUEST = 'LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'LOGOUT_SUCCESS';
export const RESET_REQUEST = 'RESET_REQUEST';
export const RESET_SUCCESS = 'RESET_SUCCESS';
export const PASSWORD_RESET_EMAIL_REQUEST = 'PASSWORD_RESET_EMAIL_REQUEST';
export const PASSWORD_RESET_EMAIL_SUCCESS = 'PASSWORD_RESET_EMAIL_SUCCESS';
export const AUTH_INIT_SUCCESS = 'AUTH_INIT_SUCCESS';
export const AUTH_INIT_ERROR = 'AUTH_INIT_ERROR';
export const AUTH_IDENTITY_ROLES_SUCCESS = 'AUTH_IDENTITY_ROLES_SUCCESS';

export const REBELITY_ADMIN_ROLE = 'Admin';
export const CLIENT_ROLE = 'Client';
export const CLIENT_MANAGER_ROLE = 'ClientManager';
export const CLIENT_EMPLOYEE_ROLE = 'ClientEmployee';
export const ASSOCIATE_ROLE = 'Associate';

/** Identity roles that may see Settings → Payout. */
export const PAYOUT_SETTINGS_ROLES = [
  REBELITY_ADMIN_ROLE,
  CLIENT_ROLE,
  CLIENT_MANAGER_ROLE,
];

const ELEVATED_PORTAL_ROLES = [
  REBELITY_ADMIN_ROLE,
  CLIENT_ROLE,
  CLIENT_MANAGER_ROLE,
];

export const PORTAL_PAYOUT_PATH = '/admin/settings/payout';
export const PORTAL_PRODUCTS_PATH = '/admin/products';

export function getIdentityRoles(authState = {}) {
  return Array.isArray(authState.identityRoles) ? authState.identityRoles : [];
}

export function hasIdentityRole(authState = {}, roleName) {
  return getIdentityRoles(authState).includes(roleName);
}

export function hasAnyIdentityRole(authState = {}, roleNames = []) {
  const roles = getIdentityRoles(authState);
  return roleNames.some((roleName) => roles.includes(roleName));
}

export function isRebelityPlatformAdmin(authState = {}) {
  return !!authState.isRebelityAdmin || hasIdentityRole(authState, REBELITY_ADMIN_ROLE);
}

export function isClientRole(authState = {}) {
  return hasIdentityRole(authState, CLIENT_ROLE);
}

export function isClientManagerRole(authState = {}) {
  return hasIdentityRole(authState, CLIENT_MANAGER_ROLE);
}

export function isClientEmployeeRole(authState = {}) {
  return hasIdentityRole(authState, CLIENT_EMPLOYEE_ROLE);
}

export function isAssociateRole(authState = {}) {
  return hasIdentityRole(authState, ASSOCIATE_ROLE);
}

export function hasElevatedPortalRole(authState = {}) {
  return isRebelityPlatformAdmin(authState)
    || hasAnyIdentityRole(authState, ELEVATED_PORTAL_ROLES);
}

/** ClientEmployee or Associate without Admin, Client, or ClientManager. */
export function isRestrictedPortalUser(authState = {}) {
  if (!authState.identityRolesLoaded) {
    return false;
  }

  if (hasElevatedPortalRole(authState)) {
    return false;
  }

  return isClientEmployeeRole(authState) || isAssociateRole(authState);
}

export function canViewTipSummaryReadOnly(authState = {}) {
  return !!authState.identityRolesLoaded && isRestrictedPortalUser(authState);
}

export function canAccessProductsMenu(authState = {}) {
  if (!authState.identityRolesLoaded) {
    return true;
  }

  if (hasElevatedPortalRole(authState)) {
    return true;
  }

  if (isAssociateRole(authState)) {
    return false;
  }

  return true;
}

export function canAccessWorkPeriodsMenu(authState = {}) {
  if (!authState.identityRolesLoaded) {
    return true;
  }

  return !isRestrictedPortalUser(authState);
}

export function canAccessUsersMenu(authState = {}) {
  if (!authState.identityRolesLoaded) {
    return true;
  }

  return !isRestrictedPortalUser(authState);
}

export function canAccessFullSettingsTabs(authState = {}) {
  if (!authState.identityRolesLoaded) {
    return true;
  }

  return !isRestrictedPortalUser(authState);
}

export function canAccessPayoutPage(authState = {}) {
  return canAccessPayoutSettings(authState) || canViewTipSummaryReadOnly(authState);
}

export function getPortalDefaultPath(authState = {}) {
  if (canViewTipSummaryReadOnly(authState)) {
    return PORTAL_PAYOUT_PATH;
  }

  return PORTAL_PRODUCTS_PATH;
}

export function getPortalLogoPath(authState = {}) {
  if (canViewTipSummaryReadOnly(authState) && canAccessProductsMenu(authState)) {
    return PORTAL_PRODUCTS_PATH;
  }

  if (canViewTipSummaryReadOnly(authState)) {
    return PORTAL_PAYOUT_PATH;
  }

  return PORTAL_PRODUCTS_PATH;
}

export function isPortalPathAllowed(pathname = '', authState = {}) {
  if (!authState.identityRolesLoaded) {
    return true;
  }

  const path = pathname || '';

  if (path.startsWith('/admin/products') && !canAccessProductsMenu(authState)) {
    return false;
  }

  if (path.startsWith('/admin/sales') && !canAccessWorkPeriodsMenu(authState)) {
    return false;
  }

  if (path.startsWith('/admin/users') && !canAccessUsersMenu(authState)) {
    return false;
  }

  if (path.startsWith('/admin/settings') && !path.startsWith(PORTAL_PAYOUT_PATH)) {
    if (!canAccessFullSettingsTabs(authState)) {
      return path.startsWith(PORTAL_PAYOUT_PATH);
    }
  }

  if (path.startsWith(PORTAL_PAYOUT_PATH) && !canAccessPayoutPage(authState)) {
    return false;
  }

  return true;
}

/** Client portal role without Admin or ClientManager — simplified payout UI only. */
export function isClientOnlyRole(authState = {}) {
  return isClientRole(authState)
    && !isRebelityPlatformAdmin(authState)
    && !isClientManagerRole(authState);
}

export function shouldUseSimplifiedPayoutDashboard(authState = {}) {
  return isClientOnlyRole(authState);
}

/** Merchant bank payout (Pay out): Admin or Client, including combined roles. */
export function canMerchantBankPayout(authState = {}) {
  return !!authState.identityRolesLoaded
    && (isRebelityPlatformAdmin(authState) || isClientRole(authState));
}

/** Bulk apply product defaults to all active products — Admin or Client only. */
export function canBulkApplyProductDefaults(authState = {}) {
  return canMerchantBankPayout(authState);
}

/** Stripe settled / pending / instant / standard payout balances — Admin or Client only. */
export function canViewMerchantBalanceDetails(authState = {}) {
  return !!authState.identityRolesLoaded
    && (isRebelityPlatformAdmin(authState) || isClientRole(authState));
}

export function canAccessPayoutSettings(authState = {}) {
  return !!authState.identityRolesLoaded
    && hasAnyIdentityRole(authState, PAYOUT_SETTINGS_ROLES);
}

export function canConfigureMerchantReserve(authState = {}) {
  return !!authState.identityRolesLoaded && isRebelityPlatformAdmin(authState);
}

export function canViewTipPayoutSettings(authState = {}) {
  return canAccessPayoutSettings(authState);
}

export function canConfigureTipPayoutRecipient(authState = {}) {
  return !!authState.identityRolesLoaded && isRebelityPlatformAdmin(authState);
}

export function canExecuteTipPayoutToRecipient(authState = {}) {
  return !!authState.identityRolesLoaded
    && (isRebelityPlatformAdmin(authState) || isClientManagerRole(authState));
}

export function buildAuthRoleState(authState = {}) {
  return {
    identityRoles: getIdentityRoles(authState),
    identityRolesLoaded: !!authState.identityRolesLoaded,
    isRebelityAdmin: isRebelityPlatformAdmin(authState),
    canViewTipSummaryReadOnly: canViewTipSummaryReadOnly(authState),
    canAccessProductsMenu: canAccessProductsMenu(authState),
    canAccessWorkPeriodsMenu: canAccessWorkPeriodsMenu(authState),
    canAccessUsersMenu: canAccessUsersMenu(authState),
    canAccessFullSettingsTabs: canAccessFullSettingsTabs(authState),
    canAccessPayoutPage: canAccessPayoutPage(authState),
    portalDefaultPath: getPortalDefaultPath(authState),
    portalLogoPath: getPortalLogoPath(authState),
  };
}
export const REGISTER_REQUEST = 'REGISTER_REQUEST';
export const REGISTER_SUCCESS = 'REGISTER_SUCCESS';

function clearStoredSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  delete axios.defaults.headers.common.Authorization;
  // Clear shared .rebelity.com cookie so Payroll SSO ends (P5 A).
  axios.post('/api/payroll/auth/logout', null, { withCredentials: true }).catch(() => {});
}

async function establishSharedAuthCookie() {
  try {
    await axios.post('/api/payroll/auth/cookie', null, { withCredentials: true });
  } catch (error) {
    // Non-blocking: local BistroAdmin session still works without cookie SSO.
    console.warn('[auth] shared payroll cookie not set', error?.message || error);
  }
}

function getStoredUser() {
  const user = localStorage.getItem('user');
  if (!user) {
    return null;
  }

  try {
    return JSON.parse(user);
  } catch (error) {
    return null;
  }
}

async function getMerchant() {
  const response = await axios.get('/api/merchant');

  if (response.data?.status === 'Error') {
    const error = new Error(response.data.message || 'Failed to retrieve merchant information.');
    error.response = {
      status: response.status,
      data: response.data,
    };
    throw error;
  }

  return normalizeMerchant(response.data);
}

async function fetchIdentityRolesFromApi() {
  try {
    const response = await axios.get('/api/Account/IdentityRoles');
    if (response.data?.status === 'Ok') {
      const roles = Array.isArray(response.data.roles) ? response.data.roles : [];
      return {
        identityRoles: roles,
        isRebelityAdmin: roles.includes(REBELITY_ADMIN_ROLE) || !!response.data.isRebelityAdmin,
        identityRolesLoaded: true,
      };
    }
  } catch (error) {
    // Non-blocking: older APIs without IdentityRoles default to hidden platform liquidity.
  }

  return {
    identityRoles: [],
    isRebelityAdmin: false,
    identityRolesLoaded: true,
  };
}

function parseIssuedSeconds(data) {
  if (data && data['.issued']) {
    const parsed = new Date(data['.issued']).getTime();
    if (!Number.isNaN(parsed)) {
      return Math.floor(parsed / 1000);
    }
  }
  return Math.floor(Date.now() / 1000);
}

function buildStoredUser(data) {
  const existingUser = getStoredUser();
  if (typeof data === 'string') {
    return existingUser || {
      user_name: '',
      expires_in: 3600,
      issued: Math.floor(Date.now() / 1000),
    };
  }

  return {
    user_name: data.userName || data.user_name || '',
    expires_in: Number(data.expires_in) || 3600,
    issued: parseIssuedSeconds(data),
  };
}

function getLoginErrorMessage(err) {
  if (err?.code === 'ECONNABORTED') {
    return 'Request timed out. Check your connection and try again.';
  }

  const apiMessage = err?.response?.data?.error_description
    || err?.response?.data?.message
    || err?.response?.data?.error;

  if (apiMessage) {
    return apiMessage;
  }

  if (!err?.response) {
    return 'Unable to reach the server. Check your connection and try again.';
  }

  return 'Login failed. Check your credentials and try again.';
}

function loadIdentityRoles(dispatch) {
  return fetchIdentityRolesFromApi().then((identity) => {
    dispatch({
      type: AUTH_IDENTITY_ROLES_SUCCESS,
      payload: identity,
    });
  });
}

export function authError(payload) {
  return {
    type: AUTH_FAILURE,
    payload,
  };
}

export function doInit() {
  return async (dispatch) => {
    const token = localStorage.getItem('token');
    let currentUser = getStoredUser();
    let merchant = null;

    if (!token) {
      dispatch({
        type: AUTH_INIT_SUCCESS,
        payload: {
          currentUser,
          merchant,
          identityRoles: [],
          isRebelityAdmin: false,
          identityRolesLoaded: true,
        },
      });

      return;
    }

    establishSharedAuthCookie();

    try {
      merchant = await getMerchant();
      console.log("merchant");
      console.log(merchant);

      dispatch({
        type: AUTH_INIT_SUCCESS,
        payload: {
          currentUser,
          merchant,
        },
      });

      loadIdentityRoles(dispatch);
    } catch (error) {
      console.log(error);

      if (error?.response?.status === 401) {
        clearStoredSession();
        currentUser = null;
      } else if (currentUser) {
        toast.error(error?.message || 'Failed to load merchant information.');
      }

      dispatch({
        type: AUTH_INIT_SUCCESS,
        payload: {
          currentUser,
          merchant: null,
          identityRoles: [],
          isRebelityAdmin: false,
          identityRolesLoaded: true,
        },
      });
    }
  }
}

export function logoutUser() {
    return (dispatch) => {
        dispatch({
          type: LOGOUT_REQUEST,
        });
        console.log("logout");
        clearStoredSession();
        dispatch({
          type: LOGOUT_SUCCESS,
        });
    };
}

export function receiveToken(data) {
    return (dispatch) => {
        const token = typeof data === 'string' ? data : data?.access_token;
        if (!token) {
          dispatch(authError('Login failed. No access token was returned.'));
          return;
        }

        const user = buildStoredUser(data);

        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;

        // Fire-and-forget shared cookie for payroll*.rebelity.com SSO.
        establishSharedAuthCookie();

        dispatch({
          type: LOGIN_SUCCESS,
          payload: { user },
        });
    }
}

export function loginUser(creds) {
    return (dispatch) => {
      dispatch({
        type: LOGIN_REQUEST,
      });

      const username = (creds?.username || '').trim();
      const password = creds?.password || '';

      if (!username || !password) {
        dispatch(authError('Please enter username and password.'));
        return;
      }

      axios.post('/token', qs.stringify({
        ...creds,
        username,
        password,
      }), { timeout: 30000 })
        .then((res) => {
          dispatch(receiveToken(res.data));
          dispatch(push('/admin/products'));
          dispatch(doInit());
        })
        .catch((err) => {
          console.error('[auth] login failed', err);
          dispatch(authError(getLoginErrorMessage(err)));
        });
    };
}